import React, { useState } from 'react';
import MaterialIcon from '@/components/MaterialIcon';
import { useTracking, isFreeEmailDomain } from '@mygpt/app/utils';

const FREE_EMAIL_ERROR = '※フリーメールアドレスはご利用いただけません。会社のメールアドレスをご入力ください。';

/**
 * RegisterFormSection組件
 * 注册表单主区域
 *
 * 左側:特典说明、导入企业信息
 * 右側:注册表单
 */
export const RegisterFormSection: React.FC = () => {
  const { trackSignupClick } = useTracking();
  const [emailError, setEmailError] = useState('');

  // フリーメール（個人メール）はネイティブ送信前に弾く
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const email = (e.currentTarget.elements.namedItem('email') as HTMLInputElement)?.value ?? '';
    if (isFreeEmailDomain(email)) {
      e.preventDefault();
      setEmailError(FREE_EMAIL_ERROR);
    }
  };

  return (
    <div className="w-full px-4 md:px-10 lg:px-40 py-16 bg-white">
      <div className="max-w-[1000px] w-full mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-solution-border overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* 左側: 特典説明 */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-600 text-xs font-bold w-fit mb-4">
                <MaterialIcon name="verified" className="text-sm" />
                完全無料
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-solution-text mb-4">
                14日間
                <br />
                無料トライアル
              </h2>
              <p className="text-solution-text-secondary mb-6">
                大型商業施設で実証済みのAIコンシェルジュを、あなたの施設でも体験いただけます。導入後も専任サポートが付きます。
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3 text-sm text-solution-text">
                  <MaterialIcon name="check_circle" className="text-solution-orange mt-0.5" />
                  <div>
                    <span className="font-bold">クレジットカード不要</span>
                    <p className="text-solution-text-secondary text-xs mt-0.5">登録時の支払い情報は必要ありません</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm text-solution-text">
                  <MaterialIcon name="check_circle" className="text-solution-orange mt-0.5" />
                  <div>
                    <span className="font-bold">最短1週間で導入</span>
                    <p className="text-solution-text-secondary text-xs mt-0.5">既存資料を読み込ませるだけで即稼働</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm text-solution-text">
                  <MaterialIcon name="check_circle" className="text-solution-orange mt-0.5" />
                  <div>
                    <span className="font-bold">専任サポート付き</span>
                    <p className="text-solution-text-secondary text-xs mt-0.5">導入から運用まで担当者がサポート</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm text-solution-text">
                  <MaterialIcon name="check_circle" className="text-solution-orange mt-0.5" />
                  <div>
                    <span className="font-bold">全機能を利用可能</span>
                    <p className="text-solution-text-secondary text-xs mt-0.5">
                      フロアマップ・FAQ・リアルタイム会話を体験
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            {/* 右側: フォーム */}
            <div className="bg-solution-bg p-8 lg:p-12 flex flex-col justify-center">
              <h3 className="text-lg font-bold text-solution-text mb-6">無料トライアルに登録</h3>
              <form className="space-y-4" action="https://cs.gbase.ai/contact" method="get" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-solution-border focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all bg-white"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-solution-text mb-2">
                      姓 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-solution-border focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all bg-white"
                      placeholder="山田"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-solution-text mb-2">
                      名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-solution-border focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all bg-white"
                      placeholder="太郎"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-solution-text-secondary mb-2">
                    ※フリーメール（gmailなど）は入力できません
                  </p>
                  <input
                    type="email"
                    name="email"
                    required
                    onChange={() => {
                      if (emailError) setEmailError('');
                    }}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      emailError ? 'border-red-500' : 'border-solution-border'
                    } focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all bg-white`}
                    placeholder="example@company.co.jp"
                  />
                  {emailError && <p className="mt-2 text-xs text-red-500">{emailError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-solution-border focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all bg-white"
                    placeholder="03-1234-5678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-solution-text mb-2">業種</label>
                  <select
                    name="industry"
                    className="w-full px-4 py-3 rounded-lg border border-solution-border focus:border-solution-orange focus:ring-2 focus:ring-solution-orange/20 outline-none transition-all bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="commercial">商業施設・百貨店</option>
                    <option value="hotel">ホテル・旅館</option>
                    <option value="ec">EC・オンラインストア</option>
                    <option value="retail">小売・店舗</option>
                    <option value="medical">医療・クリニック</option>
                    <option value="other">その他</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 rounded-lg h-14 px-8 bg-solution-orange hover:bg-solution-orange-dark transition-colors text-white text-lg font-bold shadow-lg shadow-solution-orange/20 mt-6"
                  onClick={() => trackSignupClick('register_form')}
                >
                  <MaterialIcon name="rocket_launch" />
                  無料トライアルを開始
                </button>
                <p className="text-xs text-solution-text-secondary text-center mt-4">
                  登録することで、
                  <a href="https://cs.gbase.ai/terms-of-use" className="text-solution-orange hover:underline">
                    利用規約
                  </a>
                  および
                  <a href="https://cs.gbase.ai/privacy-policy" className="text-solution-orange hover:underline">
                    プライバシーポリシー
                  </a>
                  に同意したものとみなされます。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterFormSection;
